/**
 * Nesting the roster's flat rows for rendering (BON-534).
 *
 * The server decides the hierarchy and sends rows flat, parent before child,
 * each carrying `parentId` and an absolute `depth`. This only regroups them for
 * a recursive renderer — it is presentation, not a second opinion about what
 * contains what.
 *
 * Not `utils/regionTree.ts:buildRegionForest`, which coerces everything to the
 * `RegionNode` shape and zeroes `venueCount` — the roster's whole point is the
 * figures, so passing them through that would mean looking every row up again.
 */

export interface NestableRow {
  id: string;
  parentId: string | null;
}

export interface NestedRow<T> {
  row: T;
  children: NestedRow<T>[];
}

/**
 * A forest, preserving the order rows arrived in. A row whose parent is not in
 * the list becomes a root — which is what makes a partial response (the
 * all-regions roster stops at the requested depth) render as its own tree
 * rather than silently dropping rows whose parent was never sent.
 */
export function nestRows<T extends NestableRow>(rows: readonly T[]): NestedRow<T>[] {
  const byId = new Map<string, NestedRow<T>>();
  for (const row of rows) byId.set(row.id, { row, children: [] });

  const roots: NestedRow<T>[] = [];
  for (const row of rows) {
    const node = byId.get(row.id)!;
    const parent = row.parentId ? byId.get(row.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * The same forest with every level reordered by `compare`.
 *
 * Separate from `nestRows`, which deliberately preserves the server's order:
 * a caller that wants the hierarchy as sent still gets it, and one that wants
 * it ranked says so.
 */
export function sortForest<T>(
  forest: readonly NestedRow<T>[],
  compare: (a: NestedRow<T>, b: NestedRow<T>) => number,
): NestedRow<T>[] {
  return [...forest]
    .sort(compare)
    .map((node) => ({ row: node.row, children: sortForest(node.children, compare) }));
}
